import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useAtom } from 'jotai';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalHeader,
} from '@components/Modal';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Button } from '../../tailwindComponents/Button';
import {
  BUSINESS_PROFILE_GET,
  MATCH_REQUEST_LSP_CONNECTION,
} from '../../constants';
import { useAuthHttp } from '../../hooks';
import { businessProfileAtom } from '../../store/jotai';
import {
  GOODS_PALLETISED_OPTIONS,
  OUTBOUND_UNIT_OPTIONS,
  STORAGE_UNIT_OPTIONS,
  goodsPalletisedAnswerFrom,
  palletisedGoodsPayload,
  selectedOption,
} from '../../utils/businessNeeds';

type EngageLspNeedsFormValues = {
  storageRequirement: number | null;
  storageRequirementUnitOfMeasure: string;
  productType: string;
  desiredStartDate: string;
  durationInMonths: number | null;
  goodsPalletised: string;
  palletWidthInMetres: number | null;
  palletLengthInMetres: number | null;
  palletHeightInMetres: number | null;
  palletWeightInKg: number | null;
  inboundPalletsPerWeek: number | null;
  outboundOrdersPerWeek: number | null;
  outboundUnitsPerWeek: number | null;
  outboundUnitOfMeasure: string;
};

type EngageLspModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouseId: string;
  warehouseName: string;
};

export function EngageLspModal({
  open,
  onOpenChange,
  warehouseId,
  warehouseName,
}: EngageLspModalProps) {
  const authHttp = useAuthHttp();
  const [businessProfile, setBusinessProfile] = useAtom(businessProfileAtom);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const valuesFromBusinessProfile = (): EngageLspNeedsFormValues => ({
    storageRequirement: businessProfile?.initialStorageQuantityPerWeek ?? null,
    storageRequirementUnitOfMeasure:
      businessProfile?.initialStorageRequirementUnitOfMeasure || 'Pallets',
    productType: businessProfile?.productType || '',
    desiredStartDate: businessProfile?.desiredStartDate
      ? businessProfile.desiredStartDate.slice(0, 10)
      : '',
    durationInMonths: businessProfile?.initialDurationInMonths ?? null,
    goodsPalletised: goodsPalletisedAnswerFrom(businessProfile?.goodsPalletised),
    palletWidthInMetres: businessProfile?.palletWidthInMetres ?? null,
    palletLengthInMetres: businessProfile?.palletLengthInMetres ?? null,
    palletHeightInMetres: businessProfile?.palletHeightInMetres ?? null,
    palletWeightInKg: businessProfile?.palletWeightInKg ?? null,
    inboundPalletsPerWeek:
      businessProfile?.initialInboundShipmentsPerWeek ?? null,
    outboundOrdersPerWeek:
      businessProfile?.initialOutboundShipmentsPerWeek ?? null,
    outboundUnitsPerWeek: businessProfile?.initialOutboundUnitsPerWeek ?? null,
    outboundUnitOfMeasure:
      businessProfile?.initialOutboundUnitOfMeasure || 'Pallets',
  });

  const { control, handleSubmit, reset, watch } =
    useForm<EngageLspNeedsFormValues>({
      defaultValues: valuesFromBusinessProfile(),
    });

  const goodsPalletised = watch('goodsPalletised');

  useEffect(() => {
    if (open) {
      reset(valuesFromBusinessProfile());
      setHasSubmitted(false);
      setSubmitError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const refreshBusinessProfile = async () => {
    try {
      const { data } = await authHttp.get(BUSINESS_PROFILE_GET);
      if (data?.businessProfile) {
        setBusinessProfile(data.businessProfile);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const sendConnectionRequest = async (
    needs: Record<string, unknown> | null
  ) => {
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const { data } = await authHttp.post(
        MATCH_REQUEST_LSP_CONNECTION,
        { warehouseId, needs },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data?.emailSent) {
        setHasSubmitted(true);
        if (needs) {
          await refreshBusinessProfile();
        }
      } else {
        setSubmitError(
          'We could not send your connection request. Please try again.'
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setSubmitError(
        'We could not send your connection request. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNeedsSubmit = (values: EngageLspNeedsFormValues) => {
    sendConnectionRequest({
      storageRequirement: values.storageRequirement,
      storageRequirementUnitOfMeasure:
        values.storageRequirementUnitOfMeasure || null,
      productType: values.productType || null,
      desiredStartDate: values.desiredStartDate || null,
      durationInMonths: values.durationInMonths,
      ...palletisedGoodsPayload(values),
      inboundPalletsPerWeek: values.inboundPalletsPerWeek,
      outboundOrdersPerWeek: values.outboundOrdersPerWeek,
      outboundUnitsPerWeek: values.outboundUnitsPerWeek,
      outboundUnitOfMeasure: values.outboundUnitOfMeasure || null,
    });
  };

  const numberField = (
    field: {
      value: number | null;
      onChange: (value: number | null) => void;
    },
    label: string,
    name: string,
    step?: string
  ) => (
    <Input
      name={name}
      label={label}
      type='number'
      step={step}
      placeholder='0'
      optional
      value={field.value != null ? String(field.value) : ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        field.onChange(e.target.value ? Number(e.target.value) : null)
      }
    />
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContainer preventCloseOnOutsideClick>
        <ModalHeader title={`Engage LSP - ${warehouseName}`} />
        <ModalContent
          css={{ maxHeight: 'calc(85vh - 8rem)', overflowY: 'auto' }}
        >
          {hasSubmitted ? (
            <div className='flex flex-col items-center gap-4 py-10 px-6 text-center'>
              <p className='text-base font-medium text-gray-900'>
                Your connection request has been sent
              </p>
              <p className='text-sm text-gray-500'>
                The FLOX team will contact {warehouseName} and make the
                introduction.
              </p>
              <Button
                label='Done'
                intent='primary'
                onClick={() => onOpenChange(false)}
              />
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(handleNeedsSubmit)}
              noValidate
              className='flex flex-col gap-4 p-6'
            >
              <p className='text-sm text-gray-500'>
                Tell us about your needs so we can introduce you to this
                warehouse. All fields are optional.
              </p>

              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <Controller
                  name='storageRequirement'
                  control={control}
                  render={({ field }) =>
                    numberField(field, 'Requirement', 'storageRequirement')
                  }
                />
                <Controller
                  name='storageRequirementUnitOfMeasure'
                  control={control}
                  render={({ field }) => (
                    <Select
                      label='Requirement unit'
                      optional
                      options={STORAGE_UNIT_OPTIONS}
                      selected={selectedOption(
                        STORAGE_UNIT_OPTIONS,
                        field.value
                      )}
                      setSelected={(option) => field.onChange(option.value)}
                    />
                  )}
                />

                <div className='sm:col-span-2'>
                  <Controller
                    name='productType'
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        label='Product type'
                        name='productType'
                        placeholder='e.g. food, aerosols, cosmetics'
                        optional
                      />
                    )}
                  />
                </div>

                <Controller
                  name='desiredStartDate'
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label='Desired start date'
                      name='desiredStartDate'
                      type='date'
                      optional
                    />
                  )}
                />
                <Controller
                  name='durationInMonths'
                  control={control}
                  render={({ field }) =>
                    numberField(field, 'Duration (months)', 'durationInMonths')
                  }
                />

                <Controller
                  name='goodsPalletised'
                  control={control}
                  render={({ field }) => (
                    <Select
                      label='Goods palletised'
                      optional
                      options={GOODS_PALLETISED_OPTIONS}
                      selected={selectedOption(
                        GOODS_PALLETISED_OPTIONS,
                        field.value
                      )}
                      setSelected={(option) => field.onChange(option.value)}
                    />
                  )}
                />
              </div>

              {goodsPalletised === 'Yes' && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
                  <p className='text-sm font-medium text-gray-700 mb-3'>
                    Pallet details
                  </p>
                  <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                    <Controller
                      name='palletWidthInMetres'
                      control={control}
                      render={({ field }) =>
                        numberField(
                          field,
                          'Width (m)',
                          'palletWidthInMetres',
                          '0.1'
                        )
                      }
                    />
                    <Controller
                      name='palletLengthInMetres'
                      control={control}
                      render={({ field }) =>
                        numberField(
                          field,
                          'Length (m)',
                          'palletLengthInMetres',
                          '0.1'
                        )
                      }
                    />
                    <Controller
                      name='palletHeightInMetres'
                      control={control}
                      render={({ field }) =>
                        numberField(
                          field,
                          'Height (m)',
                          'palletHeightInMetres',
                          '0.1'
                        )
                      }
                    />
                    <Controller
                      name='palletWeightInKg'
                      control={control}
                      render={({ field }) =>
                        numberField(field, 'Weight (kg)', 'palletWeightInKg')
                      }
                    />
                  </div>
                </div>
              )}

              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <Controller
                  name='inboundPalletsPerWeek'
                  control={control}
                  render={({ field }) =>
                    numberField(
                      field,
                      'Inbound flow (pallets/week)',
                      'inboundPalletsPerWeek'
                    )
                  }
                />
                <Controller
                  name='outboundOrdersPerWeek'
                  control={control}
                  render={({ field }) =>
                    numberField(
                      field,
                      'Outbound flow (orders/week)',
                      'outboundOrdersPerWeek'
                    )
                  }
                />
                <Controller
                  name='outboundUnitsPerWeek'
                  control={control}
                  render={({ field }) =>
                    numberField(
                      field,
                      'Outbound volume (UoM/week)',
                      'outboundUnitsPerWeek'
                    )
                  }
                />
                <Controller
                  name='outboundUnitOfMeasure'
                  control={control}
                  render={({ field }) => (
                    <Select
                      label='Outbound unit of measure'
                      optional
                      options={OUTBOUND_UNIT_OPTIONS}
                      selected={selectedOption(
                        OUTBOUND_UNIT_OPTIONS,
                        field.value
                      )}
                      setSelected={(option) => field.onChange(option.value)}
                    />
                  )}
                />
              </div>

              {submitError && (
                <p className='rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
                  {submitError}
                </p>
              )}

              <div className='flex items-center justify-end gap-3 border-t border-gray-200 pt-4'>
                <Button
                  label='Cancel'
                  intent='secondary'
                  type='button'
                  disabled={isSubmitting}
                  onClick={() => onOpenChange(false)}
                />
                <Button
                  label="Don't know"
                  intent='secondary'
                  type='button'
                  disabled={isSubmitting}
                  onClick={() => sendConnectionRequest(null)}
                />
                <Button
                  label={isSubmitting ? 'Sending...' : 'Submit'}
                  intent='primary'
                  type='submit'
                  disabled={isSubmitting}
                />
              </div>
            </form>
          )}
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
}
